import { Variation } from '@/@core/services/app.service'
import { TableColumnsType } from 'antd'
import React from 'react'
import { useTranslation } from 'react-i18next'
import {
  Stack,
  Typography,
  Box,
  Chip,
  IconButton,
  Badge,
  Slide
} from '@mui/material'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'

import Tooltip, { tooltipClasses, TooltipProps } from '@mui/material/Tooltip'
import { useContext } from 'react'
import { AbilityContext } from '@/layouts/components/acl/Can'
import { styled } from '@mui/material/styles'
import CommentOutline from 'mdi-material-ui/CommentOutline'
import { ExternalLinks } from '@/@core/services/api'
import {
  ChevronDown,
  ChevronRight,
  Circle,
  CircleHalfFull,
  MenuDown
} from 'mdi-material-ui'
import {
  addCommas,
  chromosomeRegion,
  formatVariantInheritance,
  isJson,
  isWindow,
  renameChildTypes,
  switchACMG,
  unique
} from '@/@core/utils/utils'
import { useRouter } from 'next/router'
import { Rating } from '@mui/material'

interface Props {
  setCurrent: any
  setOpen: any
  setHistoryOpen: any
  setCommentsOpen: any
  setTranscript_list: any
  handleMenuClick: any
  getColumnSearchProps: any
  setFilterGeneOpen: any
  setMenuType: any
  setFilterGeneData: any
  getFilterGene: any
  setIndexOpen: any
  setGeneIndexOpen: any
  setGeneOpen: any
  setAcmgOpen: any
  setOMIMOpen: any
  setIgvOpen: any
  setIgvLocus: any
  setCnvScipOpen: any
  setCnvAllOpen: any
  VariationGeneRef: any
  analysisInfo: any
  query: any
}
const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  // [`& .${tooltipClasses.tooltip}`]: {
  //   backgroundColor: 'rgba(97, 97, 97)',
  //   color: 'rgb(255, 255, 255)',
  //   maxWidth: 220,
  //   fontSize: theme.typography.pxToRem(12),
  //   border: '1px solid #dadde9'
  // }
}))

const TypographyA = styled(Typography)(() => ({
  fontSize: '0.8rem',
  color: 'inherit'
}))

const Badges = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: '-10px',
    backgroundColor: 'rgb(110,200,229)'
  },
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2)
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1)
  }
}))

const CustomWidthTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
    maxHeight: 400,
    overflow: 'auto'
  }
})

const color = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgba(229,38,35,0.8)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely pathogenic',
    color: 'rgba(221,109,32,0.8)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(46, 125, 50,0.8)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240,0.4)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200,0.8)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(109,56,149,0.8)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(237, 108, 2,0.8)'
  }
]

const GLScolor = [
  {
    key: 'pathogenic',
    value: 'pathogenic',
    color: 'rgb(218,150,148)'
  },
  {
    key: 'likely pathogenic',
    value: 'likely benign',
    color: 'rgb(245,182,193)'
  },
  {
    key: 'drug response',
    value: 'drug response',
    color: 'rgb(186,204,147)'
  },
  {
    key: 'uncertain significance',
    value: 'uncertain significance',
    color: 'rgb(240,239,240)'
  },
  {
    key: 'likely benign',
    value: 'likely benign',
    color: 'rgb(107,148,200)'
  },
  {
    key: 'benign',
    value: 'benign',
    color: 'rgb(64,113,190)'
  },
  {
    key: 'loss of function',
    value: 'loss of function',
    color: 'rgb(246,209,139)'
  }
]

export const judgeType = (str: string, arr: any[]) => {
  if (str.includes('child') && !str.includes('//')) {
    return 'C'
  } else if (str.includes('child') && str.includes('//')) {
    return `C${str.split('//')[1]}`
  } else if (str.includes('mother')) {
    return 'M'
  } else if (str.includes('father')) {
    return 'F'
  } else {
    return 'O'
  }
}

const columns1SV = React.forwardRef((props: Props, ref) => {
  const { t, i18n } = useTranslation()
  const ability = useContext(AbilityContext)

  const router = useRouter()

  const { id, ids, group } = router.query

  const type = router.asPath.includes('/samples') ? 'sample' : 'analyze'

  const column: ColumnDef<Variation>[] = React.useMemo(() => {
    if (props.analysisInfo.pipelineVersion) {
      return [
        {
          accessorKey: 'achrom',
          id: 'chrom',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chrom')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.chrom}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'start',
          id: 'start',
          enableSorting: false,
          header: () => (
            <Stack
              maxWidth={'100%'}
              justifyContent="start"
              alignItems="center"
              direction={'row'}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Start')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('End ')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    //
                    props.setIgvOpen(true)

                    props.setCurrent(row.original)

                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.start}`
                    )
                  }}
                >
                  {row.original.start}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    //
                    props.setIgvOpen(true)

                    props.setCurrent(row.original)

                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.end}`
                    )
                  }}
                >
                  {row.original.end}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'cn',
          id: 'cn',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Copy Number(CN)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.cn}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'sv_type',
          id: 'sv_type ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Type')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.sv_type}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'gene1',
          id: 'gene1',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Tooltip title={'下拉箭头查看基因详情'}>
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Gene')}`}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const gene = row.original?.gene
            const scip_gene_info_data = row.original?.scip_gene_info ?? []

            return (
              <Stack
                sx={{
                  position: 'relative'
                }}
                maxWidth={'160px'}
                direction="row"
                alignItems="center"
                justifyContent={'space-between'}
              >
                <Stack
                  maxWidth={'130px'}
                  width={'100%'}
                  marginTop={'12px'}
                  direction="column"
                  alignItems="start"
                  sx={{
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    props.setCnvAllOpen && props.setCnvAllOpen(true)

                    props.setCurrent(row.original)
                  }}
                >
                  <Tooltip title={`${gene}`} placement="top-start">
                    <TypographyA
                      sx={{
                        width: '100%',
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all',
                        fontWeight: 500
                      }}
                    >
                      {gene ? (
                        <>
                          <span>{`${gene}`}</span>
                        </>
                      ) : null}
                    </TypographyA>
                  </Tooltip>
                </Stack>

                {scip_gene_info_data.length ? (
                  <IconButton
                    onClick={(e) => {
                      props.setCurrent(row.original)

                      props.handleMenuClick &&
                        props.handleMenuClick(e, 'scip_gene')
                    }}
                    sx={{
                      color: 'inherit',
                      padding: 0

                      // position: 'absolute',
                      // right: 0,
                      // bottom: '-1rem'
                    }}
                    size="small"
                  >
                    <MenuDown />
                  </IconButton>
                ) : (
                  ''
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'coverage',
          id: 'coverage',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip
                placement="top-start"
                title={`实心圆表纯合，半实心表杂合；ref/alt`}
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Zygosity')}/${t('Coverage')}`}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {(row.original.allele_frequency ||
                  row.original.allele_frequency === 0) &&
                row.original.allele_frequency !== '.' ? (
                  <Stack direction={'row'} alignItems="center" spacing={1}>
                    <Stack maxWidth={'100%'} direction="column" spacing={1}>
                      {row.original.allele_frequency < 0.85 ? (
                        <CircleHalfFull
                          sx={{ color: 'black', fontSize: '0.875rem' }}
                        />
                      ) : (
                        <Circle sx={{ color: 'black', fontSize: '0.875rem' }} />
                      )}
                    </Stack>

                    <TypographyA
                      sx={{
                        maxWidth: '100%'
                      }}
                    >
                      {`${getValue<string>()}  [${row.original.allele_frequency?.toFixed(
                        2
                      )}] `}
                    </TypographyA>
                  </Stack>
                ) : (
                  ''
                )}
              </Stack>
            )
          }
        },
        {
          accessorKey: 'consensus',
          id: 'consensus ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Consensus')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <Tooltip placement="top-start" title={row.original.consensus}>
                  <TypographyA
                    sx={{
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {row.original.consensus}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'score_details',
          id: 'score_details ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('ACMG evidences')}(Annotsv)`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`ACMG ${t('score')}`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Length (bp)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.score_details}</TypographyA>

                <TypographyA>{row.original.total_score}</TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {typeof row.original.sv_length === 'number'
                    ? Math.round(row.original.sv_length)
                    : row.original.sv_length}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'left_genes',
          id: 'left_genes ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Left genes ')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`Left location`}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.left_genes}</TypographyA>

                <TypographyA>{row.original.left_location}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'right_genes',
          id: 'right_genes ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Right genes ')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`Right location`}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.right_genes}</TypographyA>

                <TypographyA>{row.original.right_location}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'classification',
          id: 'ccnv_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('ACMG')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const classification = row.original.classification

            const bgcolor = GLScolor.find(
              (i) => i.key === switchACMG(classification)?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === switchACMG(classification)?.toLowerCase()
                )?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="row"
                alignItems="center"
              >
                {classification && classification != '.' ? (
                  <Chip
                    sx={{
                      bgcolor: bgcolor,
                      color:
                        bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                      justifyContent: 'start',
                      fontSize: '0.75rem'
                    }}
                    label={`${classification}`}
                    onClick={() => {
                      // setAcmgOpen(true)
                      // props.setCurrent(row.original)
                    }}
                    size="small"
                  />
                ) : (
                  classification
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'exon_number',
          id: 'Exon No.',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="center" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('OMIM')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const omim_info = row.original.newOMIMInfo

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <Tooltip
                  title={
                    <Box maxHeight={300} overflow="auto">
                      {omim_info?.map((item, index) => (
                        <TypographyA key={`${item.pheno_name_cn}_${index}`}>
                          {`(${item.inheritance ? item.inheritance : '.'})-${
                            item.pheno_name_cn
                          }`}
                        </TypographyA>
                      ))}
                    </Box>
                  }
                  placement={'top-start'}
                  TransitionComponent={Slide}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: isWindow() ? '400px' : '200px'
                    }
                  }}
                >
                  <Box width={'100%'}>
                    {omim_info?.slice(0, 5).map((items, index) => {
                      return (
                        <TypographyA
                          sx={{
                            width: '100%',
                            maxWidth: 300,
                            lineHeight: '24px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            color: '#23527c',
                            mb: '4px'
                          }}
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOMIMOpen(true)
                          }}
                          key={`${items.pheno_name_cn}_${index}`}
                        >
                          <Chip
                            label={`${
                              items.inheritance ? items.inheritance : '.'
                            }`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.75rem'
                            }}
                          />

                          <span
                            style={{
                              lineHeight: '24px'
                            }}
                          >
                            {`${items.pheno_name_cn}`}
                          </span>
                        </TypographyA>
                      )
                    })}
                  </Box>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'callers',
          id: 'callers',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Callers')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const callers = row.original.callers

            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA>{callers}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        }
      ]
    } else {
      return [
        {
          accessorKey: 'achrom',
          id: 'chrom',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Chrom')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.chrom}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'SV_start',
          id: 'SV_start',
          enableSorting: false,
          header: () => (
            <Stack
              maxWidth={'100%'}
              justifyContent="start"
              alignItems="center"
              direction={'row'}
            >
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Start')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('End ')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    //
                    props.setIgvOpen(true)

                    props.setCurrent(row.original)

                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.SV_start}`
                    )
                  }}
                >
                  {row.original.SV_start}
                </TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    cursor: 'pointer'
                  }}
                  onClick={() => {
                    //
                    props.setIgvOpen(true)

                    props.setCurrent(row.original)

                    props.setIgvLocus(
                      `chr${row.original.chrom}:${row.original.SV_end}`
                    )
                  }}
                >
                  {row.original.SV_end}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'CN',
          id: 'CN',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Copy Number(CN)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'100px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="center"
              >
                <TypographyA>{row.original.CN}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'SVType',
          id: 'SVType ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Type')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <TypographyA>{row.original.SV_type}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'gene1',
          id: 'gene1',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Gene')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const omim_genes = row.original?.ExomeDepth_omim_genes?.join(';')
            const omim_disease_genes =
              row.original.gene_info?.omim_disease_genes
                .map((item) => item.symbol)
                .join(';')
            const other_genes = row.original.gene_info?.other_genes?.join(';')

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                sx={{
                  cursor: 'pointer'
                }}
                onClick={() => {
                  props.setCnvAllOpen(true)

                  props.setCurrent(row.original)
                }}
              >
                <Tooltip title={`${omim_disease_genes}`} placement="top-start">
                  <TypographyA
                    sx={{
                      width: '100%',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-all',
                      fontWeight: 500
                    }}
                  >
                    {omim_disease_genes ? (
                      <>
                        <span
                          style={{
                            color: 'red'
                          }}
                        >{`${omim_disease_genes}`}</span>
                      </>
                    ) : null}
                  </TypographyA>
                </Tooltip>

                {/* <Tooltip title={omim_genes} placement="right-start">
                <TypographyA
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {omim_genes ? `omim_genes:${omim_genes}` : null}
                </TypographyA>
              </Tooltip> */}

                <Tooltip title={other_genes} placement="top-start">
                  {/* <TypographyA
                  sx={{
                    width: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {other_genes ? `OTHER OMIM GENES:${other_genes}` : null}
                </TypographyA> */}
                  <TypographyA
                    sx={{
                      width: '100%',
                      display: '-webkit-box',
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      wordBreak: 'break-all',
                      fontWeight: 500
                    }}
                  >
                    {other_genes ? (
                      <>
                        <span>{`${other_genes}`}</span>
                      </>
                    ) : null}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'coverage',
          id: 'coverage',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Tooltip
                placement="top-start"
                title={`实心圆表纯合，半实心表杂合；ref/alt`}
              >
                <Stack maxWidth={'100%'}>
                  <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                    {`${t('Zygosity')}/${t('Coverage')}`}
                  </span>
                </Stack>
              </Tooltip>
            </Stack>
          ),
          footer: (props) => props.column.id,
          cell: ({ row, getValue }) => {
            let bgcolor = color.find(
              (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
            )
              ? (color.find(
                  (i) => i.key === row.original.CLINSIG_final?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            // loss_of_function等于1 并且CLINSIG_final不等于P/LP
            if (row.original.CLINSIG_final?.toLowerCase() === 'pathogenic') {
              //
            } else if (
              row.original.CLINSIG_final?.toLowerCase() === 'likely pathogenic'
            ) {
              //
            } else if (row.original.loss_of_function === 1) bgcolor = '#f5d18c'

            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                {(row.original.allele_frequency ||
                  row.original.allele_frequency === 0) &&
                row.original.allele_frequency !== '.' ? (
                  <Stack direction={'row'} alignItems="center" spacing={1}>
                    <Stack maxWidth={'100%'} direction="column" spacing={1}>
                      {row.original.allele_frequency < 0.85 ? (
                        <CircleHalfFull
                          sx={{ color: 'black', fontSize: '0.875rem' }}
                        />
                      ) : (
                        <Circle sx={{ color: 'black', fontSize: '0.875rem' }} />
                      )}
                    </Stack>

                    <TypographyA
                      sx={{
                        maxWidth: '100%'
                      }}
                    >
                      {`${getValue<string>()}  [${row.original.allele_frequency?.toFixed(
                        2
                      )}] `}
                    </TypographyA>
                  </Stack>
                ) : (
                  ''
                )}
              </Stack>
            )
          }
        },
        {
          accessorKey: 'consensus',
          id: 'consensus ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Consensus')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <Tooltip placement="top-start" title={row.original.consensus}>
                  <TypographyA
                    sx={{
                      maxWidth: 80,
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {row.original.consensus}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'ClassifyCNV_evidences',
          id: 'ClassifyCNV_evidences ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`${t('ACMG evidences')}(Annotsv)`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`ACMG ${t('score')}`}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Length (bp)')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.AnnotSV_evidences}</TypographyA>

                <TypographyA>{row.original.AnnotSV_ranking_score}</TypographyA>

                <TypographyA
                  sx={{
                    maxWidth: 80,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {row.original.LEN}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'left_genes',
          id: 'left_genes ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Left genes ')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`Left location`}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.left_genes}</TypographyA>

                <TypographyA>{row.original.left_location}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'right_genes',
          id: 'right_genes ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('Right genes ')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {`Right location`}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
                justifyContent={'center'}
              >
                <TypographyA>{row.original.right_genes}</TypographyA>

                <TypographyA>{row.original.right_location}</TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'classification',
          id: 'ccnv_priority',
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="start" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('ACMG')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const classification = row.original.classification

            const bgcolor = GLScolor.find(
              (i) => i.key === classification?.toLowerCase()
            )
              ? (color.find((i) => i.key === classification?.toLowerCase())
                  ?.color as
                  | 'rgb(240,239,240,0.4)'
                  | 'rgba(221,109,32,0.8)'
                  | 'rgb(255,242,202)'
                  | 'rgb(245,182,193)')
              : 'rgba(0, 0, 0)'

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="row"
                alignItems="center"
              >
                {row.original.ccnv_classification &&
                row.original.ccnv_classification != '.' ? (
                  <Chip
                    sx={{
                      bgcolor: bgcolor,
                      color:
                        bgcolor === 'rgb(240,239,240,0.4)' ? 'black' : 'white',
                      justifyContent: 'start',
                      fontSize: '0.75rem'
                    }}
                    label={`${
                      row.original.ccnv_classification &&
                      row.original.ccnv_classification != '.'
                        ? row.original.ccnv_classification
                            .split(' ')
                            .map((word) => {
                              if (word.includes('Uncertain')) return `VU`
                              if (word.includes('null')) return ``
                              else return word[0].toUpperCase()
                            })
                            .join('')
                        : '.'
                    }`}
                    onClick={() => {
                      // setAcmgOpen(true)
                      // props.setCurrent(row.original)
                    }}
                    size="small"
                  />
                ) : (
                  row.original.ccnv_classification
                )}
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'exon_number',
          id: 'Exon No.',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} justifyContent="center" direction={'row'}>
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('OMIM')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const omim_info = row.original.omim_info

            const bgcolor = color.find(
              (i) => i.key === row.original.ccnv_classification?.toLowerCase()
            )
              ? (color.find(
                  (i) =>
                    i.key === row.original.ccnv_classification?.toLowerCase()
                )?.color as any)
              : 'rgba(0, 0, 0)'

            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <Tooltip
                  title={
                    <Box maxHeight={300} overflow="auto">
                      {omim_info?.map((item, index) => (
                        <TypographyA key={`${item.pheno_name_cn}_${index}`}>
                          {`(${item.inheritance ? item.inheritance : '.'})-${
                            item.pheno_name_cn
                          }`}
                        </TypographyA>
                      ))}
                    </Box>
                  }
                  placement={'top-start'}
                  TransitionComponent={Slide}
                  sx={{
                    '& .MuiTooltip-tooltip': {
                      maxWidth: isWindow() ? '400px' : '200px'
                    }
                  }}
                >
                  <Box width={'100%'}>
                    {omim_info?.slice(0, 5).map((items, index) => {
                      return (
                        <TypographyA
                          sx={{
                            width: '100%',
                            maxWidth: 300,
                            lineHeight: '24px',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            color:
                              bgcolor === 'rgb(109,56,149,0.8)' ||
                              bgcolor === 'rgb(107,148,200,0.8)' ||
                              bgcolor === 'rgba(229,38,35,0.8)'
                                ? 'white'
                                : '#23527c',
                            mb: '4px'
                          }}
                          onClick={() => {
                            props.setCurrent(row.original)

                            props.setOMIMOpen(true)
                          }}
                          key={`${items.pheno_name_cn}_${index}`}
                        >
                          <Chip
                            label={`${
                              items.inheritance ? items.inheritance : '.'
                            }`}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.75rem'
                            }}
                          />

                          <span
                            style={{
                              lineHeight: '24px'
                            }}
                          >{`${items.pheno_name_cn}`}</span>
                        </TypographyA>
                      )
                    })}
                  </Box>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'P_snvindel_nb',
          id: 'P_snvindel_nb',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  Pathogenic
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  SNV/INDEL
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  overlapped
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack
                maxWidth={'150px'}
                width={'100%'}
                marginTop={'12px'}
                direction="column"
                alignItems="start"
              >
                <Tooltip
                  title={`${row.original.P_snvindel_nb}`}
                  placement="top-start"
                >
                  <TypographyA
                    sx={{
                      maxWidth: '100%',
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis'
                    }}
                  >
                    {`${row.original.P_snvindel_nb}`}
                  </TypographyA>
                </Tooltip>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },

        {
          accessorKey: 'DDD_HI_percent',
          id: 'DDD_HI_percent ',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('DDD HI')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('LOEUF bin')}
                </span>

                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('GnomAD pLI')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${row.original.DDD_HI_percent}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${row.original.LOEUF_bin}`}
                </TypographyA>

                <TypographyA
                  sx={{
                    width: '100%',
                    maxWidth: 140,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis'
                  }}
                >
                  {`${
                    row.original.GnomAD_pLI === '.' ||
                    row.original.GnomAD_pLI === 'NaN'
                      ? '.'
                      : (Number(row.original.GnomAD_pLI).toFixed(
                          4
                        ) as unknown as number) == 0
                      ? 0
                      : Number(row.original.GnomAD_pLI).toFixed(4)
                  }`}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        },
        {
          accessorKey: 'clinvar_info',
          id: 'Clinvar_info',
          enableSorting: false,
          header: () => (
            <Stack maxWidth={'100%'} direction={'row'} alignItems="center">
              <Stack maxWidth={'100%'}>
                <span style={{ lineHeight: 'normal', textAlign: 'left' }}>
                  {t('clinvar Info')}
                </span>
              </Stack>
            </Stack>
          ),
          cell: ({ row, getValue }) => {
            const clinvar_info: any[] = row.original.clinvar_info ?? []

            return (
              <Stack maxWidth={'100%'} direction="column" spacing={1}>
                <TypographyA
                  onClick={(e) => {
                    if (clinvar_info.length) {
                      props.setCurrent(row.original)

                      props.handleMenuClick(e, 'clinvar_info')
                    }
                  }}
                  sx={{
                    cursor: 'pointer'
                  }}
                >
                  {clinvar_info.length}
                </TypographyA>
              </Stack>
            )
          },
          footer: (props) => props.column.id
        }
      ]
    }
  }, [props])

  React.useImperativeHandle(ref, () => ({
    column
  }))

  return <></>
})

export default columns1SV
